<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Pessoas Jurídicas</h5>
      <b-button
        v-if="hasPermission('adicionar_pessoa_juridica')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-person-plus-fill" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.razao_social"
        placeholder="Filtrar por Razão Social"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.nome_fantasia"
        placeholder="Filtrar por Nome Fantasia"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.cnpj"
        placeholder="Filtrar por CNPJ"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('razao_social')">
              Razão Social {{ arrow('razao_social') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome_fantasia')">
              Nome Fantasia {{ arrow('nome_fantasia') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('cnpj')">
              CNPJ {{ arrow('cnpj') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('email')">
              E-mail {{ arrow('email') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pj in pessoas" :key="pj.id">
          <td>{{ pj.razao_social }}</td>
          <td>{{ pj.nome_fantasia }}</td>
          <td>{{ formatarCNPJ(pj.cnpj) }}</td>
          <td>{{ pj.email }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_pessoa_juridica')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalhar(pj.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_pessoa_juridica')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editar(pj.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_pessoa_juridica')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluir(pj.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// composable para pessoas jurídicas
const {
  items: pessoas,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/pessoas-juridicas`,
  { key: 'razao_social', order: 'asc' }
)

// opções de limite de página
const pageOptions = ref([10, 15, 20, 25, 50])

// Computeds para o resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

// Watchers para recarregar ao mudar página ou limite
watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

// Inicialização
onMounted(() => {
  filters.value = { razao_social: '', nome_fantasia: '', cnpj: '' }
  load()
})

// Handlers de filtro
function filtrar() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value.razao_social = ''
  filters.value.nome_fantasia = ''
  filters.value.cnpj = ''
  paginaAtual.value = 1
  load()
}

// Handlers de paginação
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// Navegação e ações
function irParaCadastro() {
  router.push('/cadastros/pessoas-juridicas/novo')
}
function detalhar(id) {
  router.push(`/cadastros/pessoas-juridicas/${id}/detalhar`)
}
function editar(id) {
  router.push(`/cadastros/pessoas-juridicas/${id}`)
}
async function excluir(id) {
  const confirmou = await proxy.$confirm(
    'Confirma exclusão desta Pessoa Jurídica?',
    'Confirmação'
  )
  if (!confirmou) return
  try {
    await axios.delete(`/cadastros/pessoas-juridicas/${id}`)
    load()
    proxy.$toast.success('Excluída com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir'
    await proxy.$alert(msg, 'Erro')
  }
}

// Formatação utilitária
function formatarCNPJ(cnpj) {
  if (!cnpj) return '—'
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  )
}
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>





